"use client";

import { useState, useEffect, useRef } from "react";
import {
  getModelSuggestions,
  searchModels,
} from "@/lib/listing/modelSuggestions";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";

interface ModelInputProps {
  value: string;
  onChange: (model: string) => void;
  brand: string | null;
  subcategory: string | null;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export default function ModelInput({
  value,
  onChange,
  brand,
  subcategory,
  placeholder = "Enter model name (optional)",
  className = "",
  disabled = false,
}: ModelInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get suggestions based on brand and subcategory
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (brand && subcategory) {
        if (searchTerm.trim()) {
          const filtered = searchModels(searchTerm, brand, subcategory);
          setSuggestions(filtered);
        } else {
          const defaultSuggestions = getModelSuggestions(brand, subcategory);
          setSuggestions(defaultSuggestions.slice(0, 8));
        }
      } else {
        setSuggestions([]);
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [searchTerm, brand, subcategory]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (model: string) => {
    onChange(model);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setSearchTerm("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    // Always update the value so user can type freely
    onChange(newValue);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    if (!searchTerm && value) {
      setSearchTerm(value);
    }
  };

  // Don't show suggestions if no brand/subcategory
  const showSuggestions =
    isOpen && !disabled && brand && subcategory && suggestions.length > 0;

  return (
    <div className={`relative w-full ${className}`} ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          disabled={disabled}
          placeholder={placeholder}
          className={`w-full px-3 py-2 pr-20 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
            disabled
              ? "bg-secondary cursor-not-allowed opacity-60"
              : "bg-surface"
          }`}
          autoComplete="off"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
          {value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 text-text-light hover:text-text-light rounded-full hover:bg-tertiary"
              aria-label="Clear model"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
          {brand && subcategory && (
            <button
              type="button"
              onClick={() => !disabled && setIsOpen(!isOpen)}
              className="p-1 text-text-light hover:text-text-light rounded-full hover:bg-tertiary"
              disabled={disabled}
              aria-label="Toggle suggestions"
            >
              <ChevronDownIcon
                className={`h-4 w-4 transition-transform ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </button>
          )}
        </div>
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && (
        <div className="absolute z-50 w-full mt-1 bg-surface border border-border rounded-lg shadow-lg max-h-60 overflow-auto">
          <ul className="py-1">
            {suggestions.map((model, index) => (
              <li key={index}>
                <button
                  type="button"
                  onClick={() => handleSelect(model)}
                  className="w-full text-left px-4 py-2 hover:bg-tertiary focus:bg-secondary focus:outline-none"
                >
                  {model}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
